/**
 * ZFS Parameters Input Component
 *
 * Input fields for ZFS tuning with compact design.
 */

import { Box, TextField, MenuItem, FormControl, Select, SelectChangeEvent, Grid, Typography } from '@mui/material';
import type { ZFSParameters } from '../../types';
import { TooltipIcon, ValidationIcon } from '../ui';
import { validateField } from '../../data/validation';

interface ZFSParametersProps {
  value: ZFSParameters;
  onChange: (value: ZFSParameters) => void;
  /** Optional: Drive size in TB (for cross-field validation of ashift) */
  driveSizeTB?: number;
  /** Optional: Workload type (for cross-field validation of recordsize) */
  workloadType?: 'database' | 'media' | 'mixed' | 'backup';
}

export function ZFSParametersInput({ value, onChange, driveSizeTB, workloadType }: ZFSParametersProps) {
  const handleTextFieldChange = (field: 'slopPercent' | 'compressionRatio') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...value, [field]: Number(event.target.value) });
  };

  const handleAshiftChange = (event: SelectChangeEvent) => {
    onChange({ ...value, ashift: Number(event.target.value) as ZFSParameters['ashift'] });
  };

  // Validations
  const ashiftValidations = validateField('ashift', value.ashift, { driveSizeTB });
  const compressionRatioValidations = validateField('compressionRatio', value.compressionRatio);
  const recordsizeValidations = value.recordsize
    ? validateField('recordsize', value.recordsize, { workloadType })
    : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Section Header */}
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
        ZFS Tuning
      </Typography>

      {/* ashift - Full Width */}
      <Box>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
          ashift (Sector Size)
          <TooltipIcon tooltipKey="ashift" />
          <ValidationIcon validations={ashiftValidations} />
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={String(value.ashift)}
            onChange={handleAshiftChange}
            error={ashiftValidations.some(v => v.severity === 'warning')}
            sx={{ fontSize: '0.8rem' }}
          >
            <MenuItem value="9" sx={{ fontSize: '0.8rem' }}>9 (512B)</MenuItem>
            <MenuItem value="10" sx={{ fontSize: '0.8rem' }}>10 (1 KiB)</MenuItem>
            <MenuItem value="11" sx={{ fontSize: '0.8rem' }}>11 (2 KiB)</MenuItem>
            <MenuItem value="12" sx={{ fontSize: '0.8rem' }}>12 (4 KiB) ✓</MenuItem>
            <MenuItem value="13" sx={{ fontSize: '0.8rem' }}>13 (8 KiB)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Slop + Compression - Horizontal */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
            Slop (%)
            <TooltipIcon tooltipKey="slop" />
          </Typography>
          <TextField
            type="number"
            value={value.slopPercent}
            onChange={handleTextFieldChange('slopPercent')}
            inputProps={{ min: 0, max: 50, step: 0.125, style: { fontSize: '0.8rem' } }}
            fullWidth
            size="small"
            sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
            Compression
            <TooltipIcon tooltipKey="compressionRatio" />
            <ValidationIcon validations={compressionRatioValidations} />
          </Typography>
          <TextField
            type="number"
            value={value.compressionRatio}
            onChange={handleTextFieldChange('compressionRatio')}
            inputProps={{ min: 1, max: 10, step: 0.1, style: { fontSize: '0.8rem' } }}
            error={compressionRatioValidations.some(v => v.severity === 'warning')}
            fullWidth
            size="small"
            sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
          />
        </Grid>
      </Grid>

      {/* Recordsize - Optional, Full Width */}
      <Box>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
          Recordsize (KiB) - Optional
          <TooltipIcon tooltipKey="recordsize" />
          <ValidationIcon validations={recordsizeValidations} />
        </Typography>
        <TextField
          type="number"
          value={value.recordsize || ''}
          onChange={(e) => onChange({
            ...value,
            recordsize: e.target.value ? Number(e.target.value) : undefined
          })}
          inputProps={{ min: 4, max: 1024, step: 4, placeholder: '128', style: { fontSize: '0.8rem' } }}
          helperText={recordsizeValidations.length > 0 ? recordsizeValidations[0].message : 'Leave empty for default (128K)'}
          error={recordsizeValidations.some(v => v.severity === 'warning')}
          fullWidth
          size="small"
          sx={{ '& .MuiInputBase-input': { py: 0.5 }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
        />
      </Box>
    </Box>
  );
}
